// Phase P-LINK-POLICE-TRIPLE
// Forward edges from /[en/]police-clearance/for/$country into the triple-key
// translation matrix /[en/]translate/english/$doctype/in/$location.
//
// Why: Police clearance for foreign visa applications almost always requires
// certified English translation of supporting documents (passport, ID, address,
// prior convictions disclosure). Deep-linking funnels PageRank from ~25
// destination-country pages (TH+EN) into the long-tail triple-key money pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

// Doctypes commonly bundled with a police-clearance application abroad.
const POLICE_DOCTYPES = [
  "police-clearance",
  "id-card",
  "house-registration",
  "passport",
  "birth-certificate",
  "marriage-certificate",
  "divorce-certificate",
  "name-change",
] as const;

const DEFAULT_HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
  "hub-chiangmai-nimman": "Chiang Mai / Nimman",
  "hub-pattaya": "Pattaya",
};

interface Props {
  mode: "th" | "en";
  countryNameTh: string;
  countryNameEn: string;
}

export default function PoliceTripleKeyLinks({ mode, countryNameTh, countryNameEn }: Props) {
  const isTh = mode === "th";

  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = POLICE_DOCTYPES
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      const hub = DEFAULT_HUBS[i % DEFAULT_HUBS.length];
      return { slug: slug as string, d, hub: hub as string };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        {isTh ? `แปลเอกสารประกอบ ยื่น ${countryNameTh}` : `Translate supporting docs → ${countryNameEn}`}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `เอกสารที่นิยมแปลภาษาอังกฤษคู่กับ Police Clearance สำหรับ${countryNameTh}`
          : `Documents most often translated into English alongside Police Clearance for ${countryNameEn}`}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? `เลือกเอกสารและฮับที่ใกล้คุณ — รับรอง MoJ + ตรา MFA พร้อมยื่นที่${countryNameTh}`
          : `Pick the document and the hub nearest you — MoJ-sworn + MFA-stamped, ready to file for ${countryNameEn}.`}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to={isTh
              ? "/translate/$language/$doctype/in/$location"
              : "/en/translate/$language/$doctype/in/$location"}
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="police-triple-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                {isTh ? `แปล ${d.nameTh} เป็นภาษาอังกฤษ` : `${d.nameEn} → English`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pick-up at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "See price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
